// Statistics about a tournament's field — how many, who is here, how strong, how old,
// where they come from — for the Statistics window on the Players page.
//
// EVERYTHING IS COUNTED THE WAY THE REST OF THE APP ALREADY COUNTS IT, so the window
// cannot disagree with the screen behind it:
//   - present is `player.status`, exactly what the roster's Present / Absent badge reads;
//   - a rating is `player.rating` as a positive number — the roster's Rating column, and
//     the number the prize categories test (js/utils/categories.js);
//   - age is age reached during the tournament's YEAR, the prize categories' convention,
//     so "15–20" here holds the same players a U20 prize would.
//
// Pure: no DOM, no state. The caller (js/ui/player-stats.js) decides which players are
// in scope — one group or the whole tournament — and hands them in.

import { playerBirthYear } from './formatting.js';
import { matchesCategory, normalizeCategories, playerSex } from './categories.js';

// Rating bands, 200 points wide through the range club tournaments actually span.
// Open-ended at both ends: a band list that stopped at 2200 would hide a GM in "2200+"
// beside a 2210 club player, and that is fine — this is a picture of the field, and the
// highest rating is shown as its own number.
const RATING_BANDS = [
    { max: 1199 },
    { min: 1200, max: 1399 },
    { min: 1400, max: 1599 },
    { min: 1600, max: 1799 },
    { min: 1800, max: 1999 },
    { min: 2000, max: 2199 },
    { min: 2200, max: 2399 },
    { min: 2400 }
];

// Age bands, cut where chess cuts them: 20 and under is a junior (U20), 50 and 65 are
// FIDE's two senior ages, and 10 and 14 are the junior classes most tournaments split.
const AGE_BANDS = [
    { max: 10 },
    { min: 11, max: 14 },
    { min: 15, max: 20 },
    { min: 21, max: 49 },
    { min: 50, max: 64 },
    { min: 65 }
];

// FIDE titles strongest first, so the open titles lead and each women's title sits after
// the open title above it — the order a chess reader expects the list in.
const TITLE_ORDER = ['GM', 'IM', 'WGM', 'FM', 'WIM', 'CM', 'WFM', 'WCM'];

/** A player's rating as a number, 0 when unrated. Same reading as categories.js. */
export function ratingOf(player) {
    const n = parseInt(player && player.rating, 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
}

/** Age reached during `year`, or null when the birth year (or the year) is unknown. */
export function ageIn(player, year) {
    const born = parseInt(playerBirthYear(player), 10);
    const y = parseInt(year, 10);
    if (!Number.isFinite(born) || !Number.isFinite(y)) return null;
    const age = y - born;
    // A birth year in the future, or one two centuries back, is a typo — not a player
    // aged -3 or 212 dragging the average with them.
    return age >= 0 && age <= 120 ? age : null;
}

function inBand(value, band) {
    return (band.min === undefined || value >= band.min) && (band.max === undefined || value <= band.max);
}

/**
 * The label a band is shown with, as a translation template for the caller to run
 * through tr() — the same contract as describeCategory() in js/utils/categories.js.
 */
function bandLabel(band, kind) {
    if (band.min === undefined) {
        return kind === 'rating'
            ? { key: 'Under {n}', params: { n: band.max + 1 } }
            : { key: '{n} and under', params: { n: band.max } };
    }
    if (band.max === undefined) return { key: '{n} and over', params: { n: band.min } };
    return { key: '{min}–{max}', params: { min: band.min, max: band.max } };
}

/**
 * Count values into bands, then drop the empty bands at either end — a club rapid with
 * nobody over 1800 should not end in four empty rows. Empty bands in the MIDDLE stay:
 * a gap in the field is part of its shape.
 */
function distribution(values, bands, kind) {
    const rows = bands.map(band => ({
        label: bandLabel(band, kind),
        min: band.min === undefined ? null : band.min,
        max: band.max === undefined ? null : band.max,
        count: values.filter(v => inBand(v, band)).length
    }));
    const first = rows.findIndex(r => r.count > 0);
    if (first === -1) return [];
    let last = rows.length - 1;
    while (rows[last].count === 0) last--;
    return rows.slice(first, last + 1);
}

/**
 * Count players by a text field: [{ name, count }], most common first, then by name.
 * Spellings that differ only in case or surrounding spaces are one entry ("Oslo SK" and
 * "oslo sk " are the same club typed twice), shown the way most players have it written
 * — the earliest spelling on a tie.
 */
function countBy(players, read) {
    const byKey = new Map();
    let missing = 0;
    players.forEach(p => {
        const raw = String(read(p) ?? '').trim();
        if (!raw) { missing++; return; }
        const key = raw.toLowerCase();
        let entry = byKey.get(key);
        if (!entry) {
            entry = { count: 0, spellings: new Map() };
            byKey.set(key, entry);
        }
        entry.count++;
        entry.spellings.set(raw, (entry.spellings.get(raw) || 0) + 1);
    });
    const rows = [...byKey.values()].map(entry => {
        let name = '';
        let best = 0;
        entry.spellings.forEach((n, spelling) => {
            if (n > best) { name = spelling; best = n; }
        });
        return { name, count: entry.count };
    });
    rows.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
    return { rows, missing };
}

function median(sorted) {
    if (sorted.length === 0) return null;
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[mid] : Math.round((sorted[mid - 1] + sorted[mid]) / 2);
}

/**
 * Every statistic the window shows, for one set of players.
 *
 * @param {Array}  players
 * @param {Object} [opts]
 * @param {number} [opts.year]        the tournament's year, for ages
 * @param {Array}  [opts.categories]  the prize categories, raw or normalised
 */
export function fieldStats(players, { year, categories } = {}) {
    const field = (players || []).filter(Boolean);
    const total = field.length;
    const present = field.filter(p => !!p.status).length;

    const ratings = field.map(ratingOf).filter(r => r > 0).sort((a, b) => a - b);
    const ages = field.map(p => ageIn(p, year)).filter(a => a !== null).sort((a, b) => a - b);

    const titles = countBy(field, p => String(p.title || '').toUpperCase());
    titles.rows.sort((a, b) => {
        const ia = TITLE_ORDER.indexOf(a.name);
        const ib = TITLE_ORDER.indexOf(b.name);
        return (ia === -1 ? TITLE_ORDER.length : ia) - (ib === -1 ? TITLE_ORDER.length : ib)
            || a.name.localeCompare(b.name);
    });

    const sexes = field.map(playerSex);
    const ctx = { year };

    return {
        total,
        present,
        absent: total - present,
        rating: {
            rated: ratings.length,
            unrated: total - ratings.length,
            average: ratings.length ? Math.round(ratings.reduce((s, r) => s + r, 0) / ratings.length) : null,
            median: median(ratings),
            highest: ratings.length ? ratings[ratings.length - 1] : null,
            lowest: ratings.length ? ratings[0] : null,
            bands: distribution(ratings, RATING_BANDS, 'rating')
        },
        age: {
            known: ages.length,
            unknown: total - ages.length,
            average: ages.length ? Math.round(ages.reduce((s, a) => s + a, 0) / ages.length) : null,
            youngest: ages.length ? ages[0] : null,
            oldest: ages.length ? ages[ages.length - 1] : null,
            bands: distribution(ages, AGE_BANDS, 'age')
        },
        sex: {
            men: sexes.filter(s => s === 'm').length,
            women: sexes.filter(s => s === 'w').length,
            unknown: sexes.filter(s => s === '').length
        },
        titles: { rows: titles.rows, untitled: titles.missing },
        clubs: countBy(field, p => p.club),
        federations: countBy(field, p => String(p.fed || '').toUpperCase()),
        classes: countBy(field, p => p.class),
        // The public flag, not the registration id: the id is private now
        // (js/utils/player-privacy.js), and a spectator's roster does not carry it.
        registeredOnline: field.filter(p => !!(p.registeredOnline || p.registrationId)).length,
        categories: normalizeCategories(categories).map(category => ({
            name: category.name,
            count: field.filter(p => matchesCategory(p, category, ctx)).length
        }))
    };
}
